import { useState } from "react";

import { http, qs } from "../api";
import {
  Badge,
  Banner,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  useAsync,
  useToast,
  type BadgeTone,
} from "../ui";
import {
  Card,
  Loadable,
  MetricCard,
  TurnBodies,
  dash,
  errMessage,
  fmtInt,
  fmtMs,
  fmtPct,
  fmtTime,
  num,
  shortId,
} from "./format";
import type { CriteriaResponse, JudgeDisagreement, ReviewRow, SuiteDetail } from "./types";

// ============================================================ review tab

/**
 * The queue is NOT "the failures" — those have their own tab. It is the turns
 * where the two graders DISAGREE, because that is where one of them is provably
 * wrong and nobody else can tell which. false_pass leads: the suite is currently
 * certifying those green.
 */
export function ReviewTab({
  detail,
  runId,
  canWrite,
}: {
  detail: SuiteDetail | null;
  runId: string;
  canWrite: boolean;
}) {
  const toast = useToast();
  const rows = detail?.review ?? [];
  const falsePass = rows.filter((r) => r.direction === "false_pass").length;
  const falseFail = rows.filter((r) => r.direction === "false_fail").length;

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Only what THIS session wrote is shown as "labelled": there is no read-back
  // endpoint for labels and the screen will not imply one.
  const [labelled, setLabelled] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const disagreement = useAsync<JudgeDisagreement>(
    () =>
      runId
        ? http.get<JudgeDisagreement>(`/chatbot/eval-ops/judge-disagreement${qs({ run_id: runId })}`)
        : Promise.resolve({}),
    [runId]
  );
  const criteria = useAsync<CriteriaResponse>(
    () => http.get<CriteriaResponse>(`/chatbot/eval-ops/criteria${qs({ limit: 20 })}`),
    []
  );

  const label = async (row: ReviewRow, verdict: "correct" | "wrong") => {
    const caseId = String(row.case_id ?? "");
    if (!caseId) {
      toast.push("This turn has no case id — nothing to label against.", "critical");
      return;
    }
    setSaving((s) => ({ ...s, [caseId]: true }));
    try {
      await http.post("/chatbot/eval-ops/labels", {
        run_id: runId,
        case_id: caseId,
        human_verdict: verdict,
        note: `eval-suite review · ${row.direction} · turn ${row.turn_index}`,
      });
      setLabelled((l) => ({ ...l, [caseId]: verdict }));
      toast.push(`Labelled ${caseId} ${verdict}`, "good");
    } catch (err) {
      toast.push(errMessage(err, "Failed to save the label"), "critical");
    } finally {
      setSaving((s) => ({ ...s, [caseId]: false }));
    }
  };

  // Recording a criteria version is governance, so the reason is mandatory —
  // the server rejects a blank one and this dialog will not send it either.
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ version: "", author: "", reason: "", drift: "", criteria: "" });
  const [formError, setFormError] = useState("");
  const [savingVersion, setSavingVersion] = useState(false);

  const saveVersion = async () => {
    if (!form.version.trim()) {
      setFormError("A version string is required.");
      return;
    }
    if (!form.reason.trim()) {
      setFormError("A criteria version must record a reason — an un-reasoned rubber-stamp is what this trail exists to prevent.");
      return;
    }
    let parsed: Record<string, unknown> = {};
    if (form.criteria.trim()) {
      try {
        const value: unknown = JSON.parse(form.criteria);
        if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("not an object");
        parsed = value as Record<string, unknown>;
      } catch {
        setFormError("Criteria must be a JSON object, or left blank.");
        return;
      }
    }
    setSavingVersion(true);
    setFormError("");
    try {
      await http.post("/chatbot/eval-ops/criteria", {
        version: form.version.trim(),
        author: form.author.trim(),
        reason: form.reason.trim(),
        drift: form.drift.trim(),
        changed: {},
        criteria: parsed,
      });
      toast.push(`Recorded criteria ${form.version.trim()}`, "good");
      setEditing(false);
      setForm({ version: "", author: "", reason: "", drift: "", criteria: "" });
      criteria.reload();
    } catch (err) {
      setFormError(errMessage(err, "Failed to record the criteria version"));
    } finally {
      setSavingVersion(false);
    }
  };

  const versions = criteria.data?.versions ?? [];
  const dis = disagreement.data;

  return (
    <div className="stack">
      <Card
        title="Review queue — where the two graders disagree"
        note={
          <>
            <span className="strong">{fmtInt(detail?.review_total)}</span> scored ·{" "}
            <span className="strong">{fmtInt(detail?.review_shown)}</span> shown. {fmtInt(falsePass)} the suite passed
            and the judge failed (hidden breakage), {fmtInt(falseFail)} the suite failed and the judge passed
            (candidate over-strict checks).
          </>
        }
      >
        {!rows.length ? (
          <Empty
            title="Nothing to review"
            body={
              !num(detail?.judge?.judged_n)
                ? "No turn in this run carries a judge verdict, so there is nothing for the gate to disagree with."
                : "The gate and the judge agreed on every judged turn in this run."
            }
          />
        ) : (
          <div className="col">
            {rows.map((row) => {
              const key = `${row.conversation_id}#${row.turn_index}`;
              const caseId = String(row.case_id ?? "");
              const tone: BadgeTone = row.direction === "false_pass" ? "critical" : "warning";
              return (
                <div key={key} className="note">
                  <div className="who" style={{ flexWrap: "wrap" }}>
                    <Badge tone={tone}>
                      {row.direction === "false_pass" ? "gate passed · judge says wrong" : "gate failed · judge says fine"}
                    </Badge>
                    <span className="name">{dash(row.case_id)}</span>
                    <span>
                      {shortId(row.conversation_id, 10)}#{row.turn_index}
                    </span>
                    <span>
                      {dash(row.lang)} · {dash(row.turn_owner)} · {fmtMs(row.latency_ms)}
                    </span>
                    <span className="spacer" />
                    {labelled[caseId] && (
                      <span style={{ color: "var(--success)" }}>labelled {labelled[caseId]}</span>
                    )}
                    {canWrite && (
                      <>
                        <button
                          className="btn sm"
                          disabled={Boolean(saving[caseId])}
                          title="The bot's reply was correct"
                          onClick={() => void label(row, "correct")}
                        >
                          Correct
                        </button>
                        <button
                          className="btn sm"
                          disabled={Boolean(saving[caseId])}
                          title="The bot's reply was wrong"
                          onClick={() => void label(row, "wrong")}
                        >
                          Wrong
                        </button>
                      </>
                    )}
                    <button className="btn sm ghost" onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}>
                      {expanded[key] ? "hide" : "show"} turn
                    </button>
                  </div>
                  <div className="body">
                    <span className="muted">gate:</span>{" "}
                    {(row.fails ?? []).join(", ") || (row.passed ? "no failing check" : "failed, no label")}
                    {" · "}
                    <span className="muted">judge:</span> {dash(row.judge_verdict)}
                  </div>
                  {row.judge_reason && <div className="body muted">{row.judge_reason}</div>}
                  {expanded[key] && <TurnBodies turn={row} />}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card
        title="Judge vs the deterministic gate, over the whole run"
        note="The same disagreement counted server-side over every turn — the list above is capped, this is not."
      >
        <Loadable loading={disagreement.loading} error={disagreement.error} what="Failed to load judge disagreement" cols={4}>
          <div className="grid c4">
            <MetricCard
              label="Agreement"
              value={fmtPct(dis?.agreement)}
              sub={`${fmtInt(dis?.agree)} of ${fmtInt(dis?.judged_n)} judged turns`}
              note={num(dis?.judged_n) === 0 ? "no turn in this run carries a judge verdict" : null}
            />
            <MetricCard
              label="False pass"
              value={fmtInt(dis?.false_pass)}
              sub="the suite is certifying these green"
              tone={num(dis?.false_pass) > 0 ? "bad" : "default"}
            />
            <MetricCard
              label="False fail"
              value={fmtInt(dis?.false_fail)}
              sub="candidate over-strict checks"
              tone={num(dis?.false_fail) > 0 ? "warn" : "default"}
            />
            <MetricCard
              label="Skipped · unjudged"
              value={`${fmtInt(dis?.skipped)} · ${fmtInt(dis?.unjudged)}`}
              sub="excluded from agreement"
              hint="A judge skip is an infrastructure failure, not a disagreement, so it never counts against the judge's reasoning."
            />
          </div>
        </Loadable>
      </Card>

      <Card
        title="Judging criteria"
        note="The rubric the judge grades against, newest version first. A verdict is only as stable as the rubric behind it — a score that moved because the criteria moved is not a change in the product. This is a mirror; the authoritative rubric is the file the harness owns."
        actions={
          canWrite ? (
            <button className="btn" onClick={() => setEditing(true)}>
              Record a version
            </button>
          ) : undefined
        }
      >
        <Loadable loading={criteria.loading} error={criteria.error} what="Failed to load the criteria history" cols={3}>
          {!versions.length ? (
            <Empty title="No criteria versions recorded" body="The harness has not posted one." />
          ) : (
            <div className="col">
              {versions.slice(0, 10).map((version, i) => (
                <div key={`${version.version ?? "v"}-${i}`} className="note">
                  <div className="who">
                    <span className="name">{dash(version.version)}</span>
                    <span>{dash(version.author)}</span>
                    <span>{fmtTime(version.ts)}</span>
                    {version.drift && <Badge tone="warning">drift: {version.drift}</Badge>}
                    {i === 0 && <Badge tone="info">current</Badge>}
                  </div>
                  {version.reason && <div className="body">{version.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </Loadable>
      </Card>

      <Dialog open={editing} onClose={() => setEditing(false)} title="Record a criteria version" wide>
        <p className="muted" style={{ fontSize: 12 }}>
          This RECORDS a rubric version; it does not author one. The authoritative rubric is the file the harness owns
          and the judge grades against — this is the mirror the dashboard reads. The trail is append-only.
        </p>
        {formError && <Banner tone="critical">{formError}</Banner>}
        <Field label="Version" required>
          <input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="v12" />
        </Field>
        <Field label="Author">
          <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
        </Field>
        <Field label="Reason" required hint="Mandatory. An un-reasoned rubber-stamp of a rubric change is what this trail exists to prevent.">
          <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} />
        </Field>
        <Field label="Drift" hint="What this version is expected to move, if anything.">
          <input value={form.drift} onChange={(e) => setForm({ ...form, drift: e.target.value })} />
        </Field>
        <Field label="Criteria JSON" hint="Optional. A JSON object, mirrored verbatim.">
          <textarea value={form.criteria} onChange={(e) => setForm({ ...form, criteria: e.target.value })} rows={6} />
        </Field>
        <DialogFooter>
          <button className="btn" onClick={() => setEditing(false)} disabled={savingVersion}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void saveVersion()} disabled={savingVersion}>
            {savingVersion && <span className="spinner" />}
            Record version
          </button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
